import React, {useCallback, useEffect, useMemo, useState} from "react"
import { makeStyles } from "@mui/styles"
import {
    computeOrderSupplierItemQuantity,
    generateStockUnitLabel
} from "../../../utils/orderReception"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material"
import DataGrid from "../../../components/DataGrid/DataGrid"
import { LOT_DAMAGE_MODE, LOT_INVENTORY_MODE, LOT_OUTPUT_MODE } from "../../../utils/lotInventoryUtils"

const useStyles = makeStyles(() => ({
    dialog: {
        "& .MuiPaper-rounded": {
          width: 560,
          padding: "35px 20px"
        }
    },
    dialogContent: {
        paddingTop: 0,
        paddingBottom: 13
    },
    cell: {
        textAlign: "center"
    },
    pointer: {
        cursor: "pointer"
    }
}))

const ProductsReceived = (props) => {
    const {
        articles,
        onInitializeOrderReception,
        canEdit,
        canInitialize,
    } = props

    const classes = useStyles()
    const [selectedArticle, setSelectedArticle] = useState(null)
    const [data, setData] = useState([])

    useEffect(() => {
        createDataObject()
    }, [articles])

    const createDataObject = useCallback(() => {
        articles && setData(articles.map(createData))
    })

    const onClickItem = useCallback((orderSupplierItemId) => {
        if (canEdit || !canInitialize) return
        const article = articles.find(article => article.supplierItem.objectId === orderSupplierItemId)
        setSelectedArticle(article)
    }, [articles, canEdit, canInitialize])

    const articleHasOutputEvents = useMemo(() => {
        if (!selectedArticle) return false
        return selectedArticle.lots.some(lot => {
            const events = lot.events || []
            const outputEvents = events.filter(e => [LOT_OUTPUT_MODE, LOT_DAMAGE_MODE].includes(e.mode))
            const inventoryEvents = events.filter(e => e.mode === LOT_INVENTORY_MODE)

            // Find RECEPTION events added by a redo (have receptionNumber)
            const receptionEvents = events.filter(e => e.mode === "RECEPTION" && e.receptionNumber != null)

            if (receptionEvents.length === 0) {
                // Case 1: First/standalone reception
                if (outputEvents.length > 0) return true
                if (inventoryEvents.length === 0) return false
                // Block only if the last inventory quantity differs from reception quantity
                const lastInventory = inventoryEvents.reduce((latest, e) => e.date > latest.date ? e : latest)
                const initialReception = events.find(e => e.mode === "RECEPTION")
                return lastInventory.quantity !== initialReception?.quantity
            }

            // Case 2: Has redo receptions — only consider events after the latest redo-reception
            const latestReceptionDate = Math.max(...receptionEvents.map(e => e.date))
            const latestReceptionEvent = receptionEvents.find(e => e.date === latestReceptionDate)

            const outputEventsAfter = outputEvents.filter(e => e.date > latestReceptionDate)
            if (outputEventsAfter.length > 0) return true

            const inventoryEventsAfter = inventoryEvents.filter(e => e.date > latestReceptionDate)
            if (inventoryEventsAfter.length === 0) return false
            // Block only if the last inventory quantity differs from latest redo-reception quantity
            const lastInventoryAfter = inventoryEventsAfter.reduce((latest, e) => e.date > latest.date ? e : latest)
            return lastInventoryAfter.quantity !== latestReceptionEvent?.quantity
        })
    }, [selectedArticle])


    const handleClose = () => {
        setSelectedArticle(null)
    }

    const handleOrderReceptionInitialize = () => {
        onInitializeOrderReception(selectedArticle.supplierItem.objectId)
        handleClose()
    }

    const createData = (orderSupplierItem) => {
        return {
            id: orderSupplierItem.supplierItem.objectId,
            name: orderSupplierItem.supplierItem.name,
            reference: orderSupplierItem.supplierItem.reference,
            quantity: computeOrderSupplierItemQuantity(orderSupplierItem) + "-" + ((orderSupplierItem.quantities && orderSupplierItem.quantities.reception) || "0") + "-" + generateStockUnitLabel(orderSupplierItem.supplierItem),
        }
    }

    const columns = [
        {
            key: "name",
            label: "Nom de l'article",
            dataType: "text",
            sortable: false,
            filter: {
                filterable: true,
                type: "text",
                fullText: true
            }
        },
        {
            key: "reference",
            label: "Références",
            dataType: "text",
            sortable: false,
            filter: {
                filterable: true,
                type: "text",
                fullText: true
            }
        },
        {
            key: "quantity",
            label: "Quantité commandée",
            dataType: "text",
            sortable: false,
            filter: {
                filterable: false,
            },
            render: (value) => {
                const quantity = value.split("-")[0]
                const receivedQuantity = value.split("-")[1]
                const unit = value.split("-")[2]
                return (
                  <div><span>{receivedQuantity} / {quantity}</span><span style={{color: "#7C7C7C"}}> ({unit})</span></div>
                )
            }
        }
    ]

    return (
        <div>
            <DataGrid
              title={"Produits réceptionnés"}
              columns={columns}
              data={data}
              withFilters={true}
              rowLabel={null}
              onRowClick={(orderSupplierItem) => onClickItem(orderSupplierItem.id)}
              withAdditionalFiltersOptions={false}
              tableContainerStyle={{boxShadow: "none"}}
            />
            <Dialog
                open={!!selectedArticle}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                className={classes.dialog}
              >
                <DialogTitle id="alert-dialog-title">
                  {
                        articleHasOutputEvents
                            ? "Impossible de recommencer la réception de cet article"
                            : " Voulez-vous recommencer la réception de cette article ?"
                    }
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {
                        articleHasOutputEvents
                        ? (
                            <Typography>
                                Un inventaire ou une sortie a déjà été effectué sur le(s) lot(s) associ(é)s à cet article. Il n'est pas possible de recommencer la réception.
                            </Typography>
                        ) : (
                            <Typography>
                                Tous les lots et avoirs générés pour cette référence seront supprimés.
                            </Typography>  
                        )
                    }
                </DialogContent>
                {
                    selectedArticle ? (
                        <DialogActions>
                            {!articleHasOutputEvents && (
                                <Button onClick={handleOrderReceptionInitialize} color="primary" variant="outlined">
                                    Recommencer
                                </Button>
                            )}
                            <Button
                                onClick={handleClose}
                                color="primary"
                                variant="contained"
                            >
                                {articleHasOutputEvents ? "Retour" : "Annuler"}
                            </Button>
                        </DialogActions>
                    ) : null
                }
              </Dialog>
        </div>
    )
}

export default ProductsReceived